// Single source of truth for each infrastructure service's numeric stat set.
//
// The full-width service card (SummaryTile, via AccountDetail) resolves its
// numeric metric cells from the descriptors here, so the field list, formatting,
// and status tone for a given service are defined exactly once. Charts, tables,
// pies, and notes live as JSX in serviceCharts.js — only the numeric widgets
// come from this module.
//
// Each descriptor is { label, get(d, accountData) -> { value, tone, hint? } }:
//   - `d`           the service's slice of accountData (accountData[key] || {})
//   - `accountData` the full payload (only cloudwatch reads it, for alarms)
//   - `value`       the already-formatted string/number to display, or null
//                   when the source field is null/undefined. A real 0 passes
//                   through as 0 (valid value); null renders the placeholder.
//                   No-fabrication: callers must render PLACEHOLDER for null,
//                   never a fabricated 0.
//   - `tone`        '' | 'warning' | 'error' | 'ok' — maps to the widget-value
//                   modifier classes (and the tile-stat tone classes).
//   - `hint`        optional short suffix (e.g. "(optimize?)").

export const PLACEHOLDER = '\u2014';

// Byte-count formatter ported verbatim from the CloudFront drill-down renderer.
const formatBytesDownloaded = (gb) => {
  const v = gb || 0;
  if (v >= 1024) return `${(v / 1024).toFixed(2)} TB`;
  if (v >= 1) return `${v.toFixed(2)} GB`;
  return `${(v * 1024).toFixed(1)} MB`;
};

export const SERVICE_STATS = {
  lambda: [
    { label: 'Total Functions', get: (d) => ({ value: d.functions || 0, tone: '' }) },
    { label: 'Invocations (24h)', get: (d) => ({ value: d.invocations != null ? d.invocations.toLocaleString() : null, tone: '' }) },
    { label: 'Error Rate', get: (d) => ({ value: d.errorRate != null ? `${d.errorRate}%` : null, tone: (d.errorRate || 0) > 1 ? 'error' : '' }) },
    { label: 'Throttles', get: (d) => ({ value: d.throttles != null ? d.throttles : null, tone: (d.throttles || 0) > 0 ? 'warning' : '' }) },
    { label: 'Avg Duration', get: (d) => ({ value: d.avgDuration != null ? `${d.avgDuration}ms` : null, tone: '' }) },
    { label: 'Max Concurrent', get: (d) => ({ value: d.maxConcurrent != null ? d.maxConcurrent : null, tone: '' }) },
  ],
  ec2: [
    { label: 'Running Instances', get: (d) => ({ value: d.running || 0, tone: '' }) },
    { label: 'Stopped Instances', get: (d) => ({ value: d.stopped || 0, tone: 'warning' }) },
    { label: 'Avg CPU (24h)', get: (d) => ({ value: d.avgCpu != null ? `${d.avgCpu}%` : null, tone: (d.avgCpu || 0) > 80 ? 'error' : (d.avgCpu || 0) > 60 ? 'warning' : '' }) },
    { label: 'Network In/Out (24h)', get: (d) => ({ value: `${d.networkIn || 0}/${d.networkOut || 0} GB`, tone: '' }) },
    { label: 'High CPU (>80%)', get: (d) => ({ value: d.highCpuInstances || 0, tone: (d.highCpuInstances || 0) > 0 ? 'error' : '' }) },
    { label: 'Low CPU (<10%)', get: (d) => ({ value: d.lowCpuInstances || 0, tone: (d.lowCpuInstances || 0) > 0 ? 'warning' : '', hint: (d.lowCpuInstances || 0) > 0 ? '(optimize?)' : undefined }) },
    { label: 'Unhealthy', get: (d) => ({ value: d.unhealthyInstances || 0, tone: (d.unhealthyInstances || 0) > 0 ? 'error' : '' }) },
    { label: 'Running >90 days', get: (d) => ({ value: d.oldInstances || 0, tone: '' }) },
  ],
  s3: [
    { label: 'Total Buckets', get: (d) => ({ value: d.buckets != null ? d.buckets : null, tone: '' }) },
    { label: 'Total Storage', get: (d) => ({ value: d.totalStorage != null ? `${d.totalStorage} TB` : null, tone: '' }) },
    { label: 'Requests (24h)', get: (d) => ({ value: d.requests != null ? d.requests.toLocaleString() : null, tone: '' }) },
    { label: 'Data Transfer', get: (d) => ({ value: `${d.dataTransfer || 0} GB`, tone: '' }) },
    { label: 'First Byte Latency', get: (d) => ({ value: d.firstByteLatencyMs != null ? `${d.firstByteLatencyMs} ms` : null, tone: '' }) },
    { label: 'Total Request Latency', get: (d) => ({ value: d.totalRequestLatencyMs != null ? `${d.totalRequestLatencyMs} ms` : null, tone: '' }) },
    { label: '5XX Errors', get: (d) => ({ value: d.errors5xx != null ? d.errors5xx.toLocaleString() : null, tone: (d.errors5xx || 0) > 0 ? 'error' : '' }) },
  ],
  rds: [
    { label: 'DB Instances', get: (d) => ({ value: d.instances || 0, tone: '' }) },
    { label: 'Connections', get: (d) => ({ value: d.connections != null ? d.connections : null, tone: '' }) },
    { label: 'CPU Utilization', get: (d) => ({ value: d.cpuUtil != null ? `${d.cpuUtil}%` : null, tone: (d.cpuUtil || 0) > 80 ? 'error' : (d.cpuUtil || 0) > 60 ? 'warning' : '' }) },
    { label: 'Storage Allocated', get: (d) => ({ value: d.storageUsed != null ? `${d.storageUsed} GB` : null, tone: '' }) },
    { label: 'Free Storage', get: (d) => ({ value: `${d.freeStorageGB || 0} GB`, tone: '' }) },
    { label: 'Read/Write IOPS', get: (d) => ({ value: `${(d.readIOPS || 0).toLocaleString()}/${(d.writeIOPS || 0).toLocaleString()}`, tone: '' }) },
    { label: 'Multi-AZ', get: (d) => ({ value: d.multiAZCount || 0, tone: '' }) },
    { label: 'Low Storage Alert', get: (d) => ({ value: d.lowStorageInstances || 0, tone: (d.lowStorageInstances || 0) > 0 ? 'error' : '', hint: (d.lowStorageInstances || 0) > 0 ? '(<20% free)' : undefined }) },
    { label: 'Max Replica Lag', get: (d) => ({ value: (d.replicaLag || 0) > 0 ? `${d.replicaLag || 0}s` : null, tone: (d.replicaLag || 0) > 60 ? 'error' : (d.replicaLag || 0) > 10 ? 'warning' : '' }) },
    { label: 'Read Latency', get: (d) => ({ value: d.readLatencySeconds != null ? `${(d.readLatencySeconds * 1000).toFixed(1)} ms` : null, tone: '' }) },
    { label: 'Write Latency', get: (d) => ({ value: d.writeLatencySeconds != null ? `${(d.writeLatencySeconds * 1000).toFixed(1)} ms` : null, tone: '' }) },
    { label: 'Instances Healthy', get: (d) => ({ value: d.instancesTotal != null ? `${d.instancesHealthy ?? 0}/${d.instancesTotal}` : null, tone: (d.instancesHealthy != null && d.instancesTotal != null && d.instancesHealthy < d.instancesTotal) ? 'warning' : '' }) },
  ],
  apigateway: [
    { label: 'Total APIs', get: (d) => ({ value: d.apis || 0, tone: '' }) },
    { label: 'Total Requests', get: (d) => ({ value: d.requests != null ? d.requests.toLocaleString() : null, tone: '' }) },
    { label: '4XX Errors', get: (d) => ({ value: d.errors4xx != null ? d.errors4xx.toLocaleString() : null, tone: (d.errors4xx || 0) > 0 ? 'warning' : '' }) },
    { label: '5XX Errors', get: (d) => ({ value: d.errors5xx != null ? d.errors5xx.toLocaleString() : null, tone: (d.errors5xx || 0) > 0 ? 'error' : '' }) },
    { label: 'Avg Latency', get: (d) => ({ value: d.latencyMsAvg != null ? `${d.latencyMsAvg} ms` : null, tone: (d.latencyMsAvg || 0) > 1000 ? 'warning' : '' }) },
    { label: 'Max Latency', get: (d) => ({ value: d.latencyMsMax != null ? `${d.latencyMsMax} ms` : null, tone: (d.latencyMsMax || 0) > 3000 ? 'error' : '' }) },
    { label: 'Integration Latency', get: (d) => ({ value: d.integrationLatencyMsAvg != null ? `${d.integrationLatencyMsAvg} ms` : null, tone: '' }) },
  ],
  dynamodb: [
    { label: 'Tables', get: (d) => ({ value: d.tables || 0, tone: '' }) },
    { label: 'Throttled Requests (24h)', get: (d) => ({ value: d.throttledRequests != null ? d.throttledRequests : null, tone: (d.throttledRequests || 0) > 0 ? 'warning' : '' }) },
    { label: 'Storage (GB)', get: (d) => ({ value: d.storageGB != null ? d.storageGB : null, tone: '' }) },
    { label: 'Request Latency', get: (d) => ({ value: d.successfulRequestLatencyMs != null ? `${d.successfulRequestLatencyMs} ms` : null, tone: (d.successfulRequestLatencyMs || 0) > 20 ? 'warning' : '' }) },
  ],
  ecs: [
    { label: 'Clusters', get: (d) => ({ value: d.clusters || 0, tone: '' }) },
    { label: 'Services', get: (d) => ({ value: d.services || 0, tone: '' }) },
    { label: 'Running Tasks', get: (d) => ({ value: d.runningTasks || 0, tone: '' }) },
    { label: 'Container Instances', get: (d) => ({ value: d.containerInstances || 0, tone: '' }) },
    { label: 'Avg CPU Utilization', get: (d) => ({ value: d.avgCpu != null ? `${d.avgCpu}%` : null, tone: (d.avgCpu || 0) > 80 ? 'error' : (d.avgCpu || 0) > 60 ? 'warning' : '' }) },
    { label: 'Avg Memory Utilization', get: (d) => ({ value: d.avgMemory != null ? `${d.avgMemory}%` : null, tone: (d.avgMemory || 0) > 80 ? 'error' : (d.avgMemory || 0) > 60 ? 'warning' : '' }) },
  ],
  stepfunctions: [
    { label: 'State Machines', get: (d) => ({ value: d.stateMachines || 0, tone: '' }) },
    { label: 'Executions (24h)', get: (d) => ({ value: d.executions != null ? d.executions : null, tone: '' }) },
    { label: 'Success Rate', get: (d) => ({ value: d.successRate != null ? `${d.successRate}%` : null, tone: '' }) },
    { label: 'Failed', get: (d) => ({ value: d.failed != null ? d.failed : null, tone: 'error' }) },
  ],
  cloudwatch: [
    { label: 'Total Alarms', get: (_d, accountData) => ({ value: ((accountData?.alarms?.ok || 0) + (accountData?.alarms?.alarm || 0)), tone: '' }) },
    { label: 'In Alarm State', get: (_d, accountData) => ({ value: accountData?.alarms?.alarm || 0, tone: (accountData?.alarms?.alarm || 0) > 0 ? 'error' : '' }) },
    { label: 'OK/Healthy', get: (_d, accountData) => ({ value: accountData?.alarms?.ok || 0, tone: 'ok' }) },
  ],
  guardduty: [
    { label: 'Detectors', get: (d) => ({ value: d.detectors || 0, tone: '' }) },
    { label: 'Findings (30d)', get: (d) => ({ value: d.findings || 0, tone: '' }) },
    { label: 'High Severity', get: (d) => ({ value: d.highSeverity || 0, tone: 'error' }) },
    { label: 'Medium Severity', get: (d) => ({ value: d.mediumSeverity || 0, tone: 'warning' }) },
  ],
  vpc: [
    { label: 'VPCs', get: (d) => ({ value: d.vpcs || 0, tone: '' }) },
    { label: 'Subnets', get: (d) => ({ value: d.subnets || 0, tone: '' }) },
    { label: 'Security Groups', get: (d) => ({ value: d.securityGroups || 0, tone: '' }) },
    { label: 'NAT Gateways', get: (d) => ({ value: d.natGateways || 0, tone: '' }) },
  ],
  elasticache: [
    { label: 'Clusters', get: (d) => ({ value: d.clusters || 0, tone: '' }) },
    { label: 'Nodes', get: (d) => ({ value: d.nodes || 0, tone: '' }) },
    { label: 'Redis', get: (d) => ({ value: d.redis || 0, tone: '' }) },
    { label: 'Memcached', get: (d) => ({ value: d.memcached || 0, tone: '' }) },
  ],
  elb: [
    { label: 'Total Load Balancers', get: (d) => ({ value: d.loadBalancers || 0, tone: '' }) },
    { label: 'Application LB', get: (d) => ({ value: d.alb || 0, tone: '' }) },
    { label: 'Network LB', get: (d) => ({ value: d.nlb || 0, tone: '' }) },
    { label: 'Classic LB', get: (d) => ({ value: d.classic || 0, tone: '' }) },
    { label: 'Target Response Time', get: (d) => ({ value: d.targetResponseTimeSeconds != null ? `${(d.targetResponseTimeSeconds * 1000).toFixed(0)} ms` : null, tone: (d.targetResponseTimeSeconds || 0) > 1 ? 'warning' : '' }) },
    { label: '5XX Errors', get: (d) => ({ value: d.errors5xx != null ? d.errors5xx.toLocaleString() : null, tone: (d.errors5xx || 0) > 0 ? 'error' : '' }) },
    { label: 'Requests (24h)', get: (d) => ({ value: d.requestCount != null ? d.requestCount.toLocaleString() : null, tone: '' }) },
  ],
  waf: [
    { label: 'Web ACLs', get: (d) => ({ value: d.webACLs || 0, tone: '' }) },
    { label: 'Rules', get: (d) => ({ value: d.rules || 0, tone: '' }) },
    { label: 'IP Sets', get: (d) => ({ value: d.ipSets || 0, tone: '' }) },
    { label: 'Status', get: (d) => ({ value: d.webACLs > 0 ? '\u2713 Active' : 'Inactive', tone: '' }) },
  ],
  sns: [
    { label: 'Topics', get: (d) => ({ value: d.topics || 0, tone: '' }) },
    { label: 'Subscriptions', get: (d) => ({ value: d.subscriptions || 0, tone: '' }) },
    { label: 'Messages (24h)', get: (d) => ({ value: (d.messagesPublished || 0).toLocaleString(), tone: '' }) },
    { label: 'Delivery Failures', get: (d) => ({ value: d.deliveryFailures || 0, tone: 'error' }) },
  ],
  sqs: [
    { label: 'Queues', get: (d) => ({ value: d.queues || 0, tone: '' }) },
    { label: 'Dead Letter Queues', get: (d) => ({ value: d.dlqCount || 0, tone: 'warning' }) },
    { label: 'Messages Visible', get: (d) => ({ value: (d.messagesVisible || 0).toLocaleString(), tone: '' }) },
    { label: 'In Flight', get: (d) => ({ value: (d.messagesInFlight || 0).toLocaleString(), tone: '' }) },
    { label: 'Messages Sent (24h)', get: (d) => ({ value: (d.messagesSent || 0).toLocaleString(), tone: '' }) },
    { label: 'Messages Received (24h)', get: (d) => ({ value: (d.messagesReceived || 0).toLocaleString(), tone: '' }) },
  ],
  cloudfront: [
    { label: 'Distributions', get: (d) => ({ value: d.distributions || 0, tone: '' }) },
    { label: 'Enabled', get: (d) => ({ value: d.enabled || 0, tone: '' }) },
    { label: 'Cache Hit Rate', get: (d) => ({ value: d.cacheHitRate != null ? `${d.cacheHitRate}%` : null, tone: '', hint: d.cacheHitRate != null ? undefined : '(metrics not enabled)' }) },
    { label: 'Error Rate (4xx/5xx)', get: (d) => ({ value: `${d.errorRate4xx || 0}% / ${d.errorRate5xx || 0}%`, tone: 'error' }) },
    { label: 'Requests (24h)', get: (d) => ({ value: (d.requests || 0).toLocaleString(), tone: '' }) },
    { label: 'Data Downloaded', get: (d) => ({ value: formatBytesDownloaded(d.bytesDownloaded), tone: '' }) },
    { label: 'Origin Latency', get: (d) => ({ value: d.originLatencyMs != null ? `${d.originLatencyMs} ms` : null, tone: (d.originLatencyMs || 0) > 500 ? 'warning' : '', hint: d.originLatencyMs != null ? undefined : '(metrics not enabled)' }) },
  ],
  secretsmanager: [
    { label: 'Total Secrets', get: (d) => ({ value: d.secrets || 0, tone: '' }) },
    { label: 'Rotation Enabled', get: (d) => ({ value: d.rotationEnabled || 0, tone: '' }) },
    { label: 'Rotation Due', get: (d) => ({ value: d.rotationDue || 0, tone: 'warning' }) },
    { label: 'Recently Rotated', get: (d) => ({ value: d.recentlyRotated || 0, tone: '' }) },
  ],
  parameterstore: [
    { label: 'Total Parameters', get: (d) => ({ value: d.parameters || 0, tone: '' }) },
    { label: 'SecureStrings', get: (d) => ({ value: d.secureStrings || 0, tone: '' }) },
    { label: 'Strings', get: (d) => ({ value: d.strings || 0, tone: '' }) },
    { label: 'Advanced Tier', get: (d) => ({ value: d.advancedTier || 0, tone: '' }) },
  ],
  ecr: [
    { label: 'Repositories', get: (d) => ({ value: d.repositories || 0, tone: '' }) },
    { label: 'Images', get: (d) => ({ value: d.images || 0, tone: '' }) },
    { label: 'Total Size', get: (d) => ({ value: `${d.totalSizeGB || 0} GB`, tone: '' }) },
    { label: 'Critical Vulnerabilities', get: (d) => ({ value: d.criticalVulnerabilities || 0, tone: 'error' }) },
    { label: 'Scan-on-Push Enabled', get: (d) => ({ value: `${d.scanEnabled || 0}/${d.repositories || 0}`, tone: '' }) },
    { label: 'High Vulnerabilities', get: (d) => ({ value: d.highVulnerabilities || 0, tone: (d.highVulnerabilities || 0) > 0 ? 'warning' : '' }) },
    { label: 'Avg Images / Repo', get: (d) => ({ value: (d.repositories || 0) > 0 ? Math.round((d.images || 0) / d.repositories) : 0, tone: '' }) },
    { label: 'Avg Size / Image', get: (d) => ({ value: (d.images || 0) > 0 ? `${((d.totalSizeGB || 0) / d.images * 1024).toFixed(0)} MB` : null, tone: '' }) },
  ],
  eventbridge: [
    { label: 'Rules', get: (d) => ({ value: d.rules || 0, tone: '' }) },
    { label: 'Enabled Rules', get: (d) => ({ value: d.enabledRules || 0, tone: '' }) },
    { label: 'Invocations (24h)', get: (d) => ({ value: (d.invocations || 0).toLocaleString(), tone: '' }) },
    { label: 'Failed Invocations', get: (d) => ({ value: d.failedInvocations || 0, tone: 'error' }) },
    { label: 'Scheduled Rules', get: (d) => ({ value: d.scheduledRules || 0, tone: '' }) },
    { label: 'Event-Pattern Rules', get: (d) => ({ value: d.eventPatternRules || 0, tone: '' }) },
    { label: 'Event Buses', get: (d) => ({ value: d.eventBuses || 0, tone: '' }) },
    { label: 'Disabled Rules', get: (d) => ({ value: Math.max(0, (d.rules || 0) - (d.enabledRules || 0)), tone: ((d.rules || 0) - (d.enabledRules || 0)) > 0 ? 'warning' : '' }) },
  ],
  kinesis: [
    { label: 'Data Streams', get: (d) => ({ value: d.streams || 0, tone: '' }) },
    { label: 'Total Shards', get: (d) => ({ value: d.shards || 0, tone: '' }) },
    { label: 'Incoming Records (24h)', get: (d) => ({ value: (d.incomingRecords || 0).toLocaleString(), tone: '' }) },
    { label: 'Max Iterator Age', get: (d) => ({ value: `${d.maxIteratorAgeSec || 0}s`, tone: (d.maxIteratorAgeSec || 0) > 60 ? 'warning' : '' }) },
  ],
  glue: [
    { label: 'ETL Jobs', get: (d) => ({ value: d.jobs || 0, tone: '' }) },
    { label: 'Running Jobs', get: (d) => ({ value: d.runningJobs || 0, tone: '' }) },
    { label: 'Succeeded (24h)', get: (d) => ({ value: d.succeededJobs24h || 0, tone: '' }) },
    { label: 'Failed (24h)', get: (d) => ({ value: d.failedJobs24h || 0, tone: 'error' }) },
    { label: 'Crawlers', get: (d) => ({ value: d.crawlers || 0, tone: '' }) },
    { label: 'Running Crawlers', get: (d) => ({ value: d.runningCrawlers || 0, tone: '' }) },
    { label: 'Databases', get: (d) => ({ value: d.databases || 0, tone: '' }) },
  ],
  mwaa: [
    { label: 'Environments', get: (d) => ({ value: d.environments || 0, tone: '' }) },
    { label: 'Healthy', get: (d) => ({ value: d.healthyEnvironments || 0, tone: '' }) },
    { label: 'Unhealthy', get: (d) => ({ value: d.unhealthyEnvironments || 0, tone: (d.unhealthyEnvironments || 0) > 0 ? 'error' : '' }) },
    { label: 'DAG Count', get: (d) => ({ value: d.dagCount || 0, tone: '' }) },
    { label: 'Tasks Running', get: (d) => ({ value: d.taskRunning || 0, tone: '' }) },
    { label: 'Tasks Queued', get: (d) => ({ value: d.taskQueued || 0, tone: (d.taskQueued || 0) > 10 ? 'warning' : '' }) },
    { label: 'Success (24h)', get: (d) => ({ value: d.taskSuccess24h || 0, tone: '' }) },
    { label: 'Failed (24h)', get: (d) => ({ value: d.taskFailed24h || 0, tone: (d.taskFailed24h || 0) > 0 ? 'error' : '' }) },
    { label: 'Success Rate', get: (d) => ({ value: `${d.successRate || 0}%`, tone: (d.successRate || 100) < 90 ? 'error' : (d.successRate || 100) < 95 ? 'warning' : '' }) },
    { label: 'Scheduler Healthy', get: (d) => ({ value: `${d.schedulerHealthy || 0}/${d.environments || 0}`, tone: (d.schedulerHealthy || 0) < (d.environments || 0) ? 'warning' : '' }) },
  ],
  backup: [
    { label: 'Backup Vaults', get: (d) => ({ value: d.vaults || 0, tone: '' }) },
    { label: 'Backup Plans', get: (d) => ({ value: d.plans || 0, tone: '' }) },
    { label: 'Protected Resources', get: (d) => ({ value: d.protectedResources || 0, tone: '' }) },
    { label: 'Running Jobs', get: (d) => ({ value: d.runningJobs || 0, tone: '' }) },
    { label: 'Completed (24h)', get: (d) => ({ value: d.completedJobs24h || 0, tone: 'ok' }) },
    { label: 'Failed (24h)', get: (d) => ({ value: d.failedJobs24h || 0, tone: (d.failedJobs24h || 0) > 0 ? 'error' : '' }) },
  ],
  config: [
    { label: 'Recorder Status', get: (d) => ({ value: d.recorderStatus || 'UNKNOWN', tone: d.recorderStatus === 'ON' ? '' : 'warning' }) },
    { label: 'Total Rules', get: (d) => ({ value: d.totalRules || 0, tone: '' }) },
    { label: 'Compliant Rules', get: (d) => ({ value: d.compliantRules || 0, tone: 'ok' }) },
    { label: 'Non-Compliant Rules', get: (d) => ({ value: d.nonCompliantRules || 0, tone: (d.nonCompliantRules || 0) > 0 ? 'error' : '' }) },
    { label: 'Non-Compliant Resources', get: (d) => ({ value: d.nonCompliantResources || 0, tone: (d.nonCompliantResources || 0) > 0 ? 'warning' : '' }) },
  ],
  codepipeline: [
    { label: 'Total Pipelines', get: (d) => ({ value: d.pipelines || 0, tone: '' }) },
    { label: 'Succeeded', get: (d) => ({ value: d.succeeded || 0, tone: 'ok' }) },
    { label: 'Failed', get: (d) => ({ value: d.failed || 0, tone: (d.failed || 0) > 0 ? 'error' : '' }) },
    { label: 'In Progress', get: (d) => ({ value: d.inProgress || 0, tone: 'warning' }) },
  ],
  msk: [
    { label: 'Clusters', get: (d) => ({ value: d.clusters || 0, tone: '' }) },
    { label: 'Active Clusters', get: (d) => ({ value: d.activeClusters || 0, tone: 'ok' }) },
    { label: 'Total Brokers', get: (d) => ({ value: d.totalBrokers || 0, tone: '' }) },
    { label: 'Serverless', get: (d) => ({ value: d.serverlessClusters || 0, tone: '' }) },
    { label: 'Max Disk Used', get: (d) => ({ value: d.maxDiskUsedPercent != null ? `${d.maxDiskUsedPercent}%` : null, tone: (d.maxDiskUsedPercent || 0) > 70 ? 'warning' : '' }) },
    { label: 'Max Broker CPU', get: (d) => ({ value: d.maxBrokerCpuPercent != null ? `${d.maxBrokerCpuPercent}%` : null, tone: (d.maxBrokerCpuPercent || 0) > 80 ? 'warning' : '' }) },
    { label: 'Under-replicated Partitions', get: (d) => ({ value: d.underReplicatedPartitions != null ? d.underReplicatedPartitions : null, tone: (d.underReplicatedPartitions || 0) > 0 ? 'warning' : 'ok' }) },
  ],
  acm: [
    { label: 'Total Certificates', get: (d) => ({ value: d.total || 0, tone: '' }) },
    { label: 'Issued', get: (d) => ({ value: d.issued || 0, tone: 'ok' }) },
    { label: 'Expiring Soon (30d)', get: (d) => ({ value: d.expiringSoon || 0, tone: (d.expiringSoon || 0) > 0 ? 'warning' : '' }) },
    { label: 'Expired', get: (d) => ({ value: d.expired || 0, tone: (d.expired || 0) > 0 ? 'error' : '' }) },
  ],
  accessanalyzer: [
    { label: 'Analyzers', get: (d) => ({ value: d.analyzers || 0, tone: '' }) },
    { label: 'Active Analyzers', get: (d) => ({ value: d.activeAnalyzers || 0, tone: 'ok' }) },
    { label: 'Active Findings', get: (d) => ({ value: d.activeFindings || 0, tone: (d.activeFindings || 0) > 0 ? 'warning' : '' }) },
  ],
  kms: [
    { label: 'Total Keys', get: (d) => ({ value: d.total || 0, tone: '' }) },
    { label: 'Customer Managed', get: (d) => ({ value: d.customerManaged || 0, tone: '' }) },
    { label: 'AWS Managed', get: (d) => ({ value: d.awsManaged || 0, tone: '' }) },
    { label: 'Pending Deletion', get: (d) => ({ value: d.pendingDeletion || 0, tone: (d.pendingDeletion || 0) > 0 ? 'warning' : '' }) },
  ],
  opensearch: [
    { label: 'Domains', get: (d) => ({ value: d.domains || 0, tone: '' }) },
    { label: 'Active Domains', get: (d) => ({ value: d.activeDomains || 0, tone: 'ok' }) },
  ],
  inspector: [
    { label: 'Active Findings', get: (d) => ({ value: d.activeFindings || 0, tone: (d.activeFindings || 0) > 0 ? 'warning' : '' }) },
    { label: 'Critical', get: (d) => ({ value: d.critical || 0, tone: (d.critical || 0) > 0 ? 'error' : '' }) },
    { label: 'High', get: (d) => ({ value: d.high || 0, tone: (d.high || 0) > 0 ? 'warning' : '' }) },
    { label: 'Medium', get: (d) => ({ value: d.medium || 0, tone: '' }) },
  ],
  transfer: [
    { label: 'Servers', get: (d) => ({ value: d.servers || 0, tone: '' }) },
    { label: 'Online Servers', get: (d) => ({ value: d.onlineServers || 0, tone: 'ok' }) },
  ],
  appflow: [
    { label: 'Flows', get: (d) => ({ value: d.flows || 0, tone: '' }) },
    { label: 'Active Flows', get: (d) => ({ value: d.activeFlows || 0, tone: 'ok' }) },
  ],
  securityhub: [
    { label: 'Enabled', get: (d) => ({ value: (d.enabled || 0) > 0 ? 'Yes' : 'No', tone: (d.enabled || 0) > 0 ? 'ok' : '' }) },
    { label: 'Active Findings', get: (d) => ({ value: d.activeFindings || 0, tone: (d.activeFindings || 0) > 0 ? 'warning' : '' }) },
    { label: 'Critical', get: (d) => ({ value: d.critical || 0, tone: (d.critical || 0) > 0 ? 'error' : '' }) },
    { label: 'High', get: (d) => ({ value: d.high || 0, tone: (d.high || 0) > 0 ? 'warning' : '' }) },
  ],
  dms: [
    { label: 'Replication Instances', get: (d) => ({ value: d.replicationInstances || 0, tone: '' }) },
    { label: 'Running Tasks', get: (d) => ({ value: d.runningTasks || 0, tone: 'ok' }) },
    { label: 'Stopped Tasks', get: (d) => ({ value: d.stoppedTasks || 0, tone: '' }) },
    { label: 'Failed Tasks', get: (d) => ({ value: d.failedTasks || 0, tone: (d.failedTasks || 0) > 0 ? 'error' : '' }) },
  ],
};

// Resolve a service's descriptor list into concrete cells for rendering.
// Returns [] for an unknown key. Each cell is { label, value, tone, hint },
// where value is null when the source field was null/undefined (callers render
// PLACEHOLDER, never a fabricated 0).
export function getServiceStats(serviceKey, d, accountData) {
  const descriptors = SERVICE_STATS[serviceKey];
  if (!descriptors) return [];
  const data = d || {};
  return descriptors.map((desc) => {
    const resolved = desc.get(data, accountData) || {};
    return {
      label: desc.label,
      value: resolved.value,
      tone: resolved.tone || '',
      hint: resolved.hint,
    };
  });
}
